import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, firstValueFrom } from 'rxjs';
import { PlanManagementApiService } from 'src/app/admin/pages/plan-management/data/plan-management-api.service';
import { InsertionPlans, IsoDate, PlanView } from 'src/app/admin/pages/plan-management/data/plan-management.models';
import { PlanNameCatalogService } from 'src/app/admin/pages/plan-management/data/plan-name-catalog.service';
import { NameCatalog, toIsoDate } from 'src/app/admin/pages/plan-management/domain/plan-presentation';
import { InsertionPlanChoiceState } from './insertion-plan-choice.state';
import { PlanChoiceDialogComponent, PlanChoiceDialogData } from './plan-choice/plan-choice-dialog.component';
import { nameCatalog } from './plan-choice/plan-choice-list.component';

/**
 * Which plan new inspections go to when two or more open plans hold their day. The Plan
 * Assistant offers the choice before inserting; when it was dismissed, or the day changed
 * since, the user is asked right before the inspections are placed. Inserts that ask no
 * one (imports, offline replay, dragging) leave the choice to the save.
 */
@Injectable({ providedIn: 'root' })
export class InsertionPlanChoiceService {
  private readonly api = inject(PlanManagementApiService);
  private readonly names = inject(PlanNameCatalogService);
  private readonly dialog = inject(MatDialog);
  private readonly state = inject(InsertionPlanChoiceState);

  /** Region, type and day already asked about that no two plans hold, so each click does not ask again. */
  private readonly unshared = new Set<string>();

  /** A new insertion (the Plan Assistant opened): earlier choices and answers no longer apply. */
  reset(): void {
    this.state.clear();
    this.unshared.clear();
  }

  choose(plan: PlanView): void {
    this.state.choose(plan);
  }

  preferredPlanIds(): string[] {
    return this.state.preferredPlanIds();
  }

  /**
   * Before a bot insertion is requested: asks as confirm does, and names the chosen plans on
   * the request, since the bot saves its points on the server afterwards. False when the
   * user cancels, and then nothing is requested.
   */
  async prepareBotRequest(request: BotInsertionRequest): Promise<boolean> {
    const regionIds = (request.GeneralSettings?.Regions ?? []).map(region => region?.RegionId);
    // The points are planned on their rows' day, which can differ from the request's date
    // when the timeline moved after the inspection type was picked.
    const rowDays = new Map<IsoDate, Date>();
    for (const row of request.CheckLists ?? []) {
      if (row?.Date) rowDays.set(toIsoDate(new Date(row.Date)), new Date(row.Date));
    }
    for (const day of rowDays.size ? [...rowDays.values()] : [request.Date]) {
      if (!(await this.confirm(request.AssetId, regionIds, request.InspectionTypeId, day))) return false;
    }
    request.PreferredPlanIds = this.preferredPlanIds();
    return true;
  }

  insertionPlans(assetId: string, regionIds: readonly string[], inspectionTypeId: string, day: IsoDate): Promise<InsertionPlans[]> {
    return firstValueFrom(this.api.getInsertionPlans(assetId, regionIds, inspectionTypeId, day));
  }

  /** The names a plan view does not carry, filled in as each catalog answers. */
  catalog(assetId: string, plans: readonly PlanView[]): Observable<NameCatalog> {
    return nameCatalog(this.names, assetId, plans);
  }

  /**
   * Asked before inspections of a type are inserted in these regions on a day. Where two or
   * more open plans hold the day and none chosen for the region does, the user picks one.
   * False when the user closes the question without choosing: nothing is inserted.
   * Offline, or when the plans cannot be read, the insert goes ahead and the save picks.
   */
  async confirm(
    assetId: string,
    regionIds: readonly (string | null | undefined)[],
    inspectionTypeId: string,
    date: Date | string
  ): Promise<boolean> {
    if (!navigator.onLine || !assetId || !inspectionTypeId || !date) return true;
    const day = toIsoDate(new Date(date));
    const unknown = [...new Set(regionIds.filter((id): id is string => !!id))].filter(
      regionId => !this.unshared.has(unsharedKey(regionId, inspectionTypeId, day))
        && !this.state.chosenFor(regionId, inspectionTypeId, day)
    );
    if (!unknown.length) return true;

    let answers: InsertionPlans[];
    try {
      answers = await this.insertionPlans(assetId, unknown, inspectionTypeId, day);
    } catch {
      // Not asked again for these this session: the save picks, and each click must not wait
      // on a read that keeps failing.
      unknown.forEach(regionId => this.unshared.add(unsharedKey(regionId, inspectionTypeId, day)));
      return true;
    }

    const shared = answers.filter(answer => answer.Plans.length > 1);
    answers
      .filter(answer => answer.Plans.length <= 1)
      .forEach(answer => this.unshared.add(unsharedKey(answer.RegionId, inspectionTypeId, day)));
    if (!shared.length) return true;

    const picked = await firstValueFrom(
      this.dialog
        .open<PlanChoiceDialogComponent, PlanChoiceDialogData, PlanView[] | undefined>(PlanChoiceDialogComponent, {
          data: { assetId, day, answers: shared },
          width: '560px',
          maxWidth: '95vw',
          autoFocus: false
        })
        .afterClosed()
    );
    if (!picked?.length) return false;
    picked.forEach(plan => this.state.choose(plan));
    return true;
  }
}

/** What a bot insertion request says about where its points go. */
export interface BotInsertionRequest {
  AssetId: string;
  InspectionTypeId: string;
  Date: Date | string;
  GeneralSettings?: { Regions?: { RegionId?: string }[] };
  /** The rows the points get, dated with the day they are planned on. */
  CheckLists?: { Date?: Date | string }[];
  PreferredPlanIds?: string[];
}

function unsharedKey(regionId: string, inspectionTypeId: string, day: IsoDate): string {
  return `${regionId}|${inspectionTypeId}|${day}`;
}
